import * as t from "io-ts";
import { UUID as uuidCodec } from "io-ts-types/UUID";

import { DonationFlowPaymentOption } from "../entity/types";

import { donationFrequencyCodec } from "./entities";
import { safeIntCodec } from "./number";

import { stringEnumCodec } from ".";

/**
 * RequirementLevel for address and donorContactInfo fields
 */
export const requirementLevelCodec = t.union([
  t.literal("required"),
  t.literal("optional"),
  t.null,
]);
export type RequirementLevel = t.TypeOf<typeof requirementLevelCodec>;

/**
 * Custom field types for donation flow customization
 */
export enum CustomFieldType {
  TEXT_AREA = "textArea",
  DROPDOWN = "dropdown",
}

export const customFieldTypeCodec = stringEnumCodec({
  name: "CustomFieldType",
  enumObject: CustomFieldType,
});

/**
 * Custom field codec - supports text area and dropdown fields
 */
export const customFieldCodec = t.intersection([
  t.type({
    type: customFieldTypeCodec,
    heading: t.string,
    required: t.boolean,
  }),
  t.partial({
    placeholder: t.string,
    options: t.array(t.string), // For dropdown type
  }),
]);
export type CustomField = t.TypeOf<typeof customFieldCodec>;

/**
 * Main customization JSONB codec for DonationFlowCustomization entity
 */
export const donationFlowCustomizationDataCodec = t.partial({
  paymentMethods: t.array(
    stringEnumCodec({
      name: "DonationFlowPaymentOption",
      enumObject: DonationFlowPaymentOption,
    })
  ),
  address: requirementLevelCodec,
  fields: t.array(customFieldCodec),
  themeColor: t.string,
  designation: t.string,
  donorContactInfo: requirementLevelCodec,
  fixedAmount: safeIntCodec,
  frequency: donationFrequencyCodec,
  minAmount: safeIntCodec,
  suggestedAmounts: t.array(safeIntCodec),
});
export type DonationFlowCustomizationData = t.TypeOf<
  typeof donationFlowCustomizationDataCodec
>;

/**
 * Response codec for API responses
 */
export const donationFlowCustomizationResponseCodec = t.intersection([
  t.type({
    id: uuidCodec,
    nonprofitId: uuidCodec,
    active: t.boolean,
    createdAt: t.string,
    updatedAt: t.string,
    customization: donationFlowCustomizationDataCodec,
  }),
  t.partial({
    fundraiserId: t.union([uuidCodec, t.null]),
    code: t.union([t.string, t.null]),
  }),
]);
export type DonationFlowCustomizationResponse = t.TypeOf<
  typeof donationFlowCustomizationResponseCodec
>;

/**
 * Custom field responses codec for DonationMetadata
 * Maps field heading to user's response
 */
export const customFieldResponsesCodec = t.record(t.string, t.string);
export type CustomFieldResponses = t.TypeOf<typeof customFieldResponsesCodec>;
